import * as XLSX from 'xlsx-js-style';

/** Light green header (Ainsoft-ish). */
const HEADER_FILL = { patternType: 'solid', fgColor: { rgb: 'C8E6C9' } };
const HEADER_FONT = { bold: true, color: { rgb: '1B5E20' }, sz: 11 };
const HEADER_ALIGN = { horizontal: 'center', vertical: 'center', wrapText: true };

const GROUP_FILL = { patternType: 'solid', fgColor: { rgb: 'E8F5E9' } };
const GROUP_FONT = { bold: true, color: { rgb: '2E7D32' }, sz: 11 };

const colLetter = (index) => {
  let n = index;
  let s = '';
  while (n >= 0) {
    s = String.fromCharCode((n % 26) + 65) + s;
    n = Math.floor(n / 26) - 1;
  }
  return s;
};

const styleCell = (ws, address, style) => {
  if (!ws[address]) ws[address] = { t: 's', v: '' };
  ws[address].s = { ...(ws[address].s || {}), ...style };
};

/**
 * Export Excel with light-green header row.
 * When `groups` have labels (grouped view), inserts group banner rows and keeps group order.
 *
 * @param {object} opts
 * @param {string} opts.sheetName
 * @param {string} opts.fileName - without path; .xlsx added if missing
 * @param {string[]} opts.headers
 * @param {{ label: string|null, key?: any, rows: any[] }[]} opts.groups
 * @param {string} [opts.groupFieldTitle]
 * @param {(row: any, serial: number) => (string|number|null|undefined)[]} opts.mapRow
 */
export const exportGroupedExcel = ({
  sheetName = 'Sheet1',
  fileName = 'export.xlsx',
  headers = [],
  groups = [],
  groupFieldTitle = 'Group',
  mapRow,
}) => {
  if (!headers.length || typeof mapRow !== 'function') {
    throw new Error('exportGroupedExcel requires headers and mapRow');
  }

  const list = Array.isArray(groups) && groups.length > 0
    ? groups
    : [{ key: null, label: null, rows: [] }];

  const aoa = [headers];
  const groupRowIndexes = [];
  let serial = 0;

  list.forEach((group) => {
    if (group.label != null) {
      const title = `${groupFieldTitle}: ${group.label} (${(group.rows || []).length})`;
      const row = Array(headers.length).fill('');
      row[0] = title;
      aoa.push(row);
      groupRowIndexes.push(aoa.length - 1);
    }
    (group.rows || []).forEach((row) => {
      serial += 1;
      const cells = mapRow(row, serial);
      const padded = Array.from({ length: headers.length }, (_, i) => {
        const v = cells[i];
        return v === null || v === undefined || v === '' ? 'N/A' : v;
      });
      aoa.push(padded);
    });
  });

  if (aoa.length <= 1) {
    throw new Error('No rows to export');
  }

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  const lastCol = headers.length - 1;

  // Header styles
  for (let c = 0; c <= lastCol; c += 1) {
    styleCell(ws, `${colLetter(c)}1`, {
      fill: HEADER_FILL,
      font: HEADER_FONT,
      alignment: HEADER_ALIGN,
    });
  }

  // Group banner styles + soft merge across columns
  if (!ws['!merges']) ws['!merges'] = [];
  groupRowIndexes.forEach((rowIndex) => {
    const excelRow = rowIndex + 1; // 1-based
    for (let c = 0; c <= lastCol; c += 1) {
      styleCell(ws, `${colLetter(c)}${excelRow}`, {
        fill: GROUP_FILL,
        font: GROUP_FONT,
        alignment: { horizontal: 'left', vertical: 'center' },
      });
    }
    if (lastCol > 0) {
      ws['!merges'].push({
        s: { r: rowIndex, c: 0 },
        e: { r: rowIndex, c: lastCol },
      });
    }
  });

  // Rough column widths from headers
  ws['!cols'] = headers.map((h) => ({
    wch: Math.min(40, Math.max(12, String(h).length + 4)),
  }));

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName.slice(0, 31));
  const name = fileName.toLowerCase().endsWith('.xlsx') ? fileName : `${fileName}.xlsx`;
  XLSX.writeFile(wb, name);
};

/** Convenience: flat list → single ungrouped sheet (still green headers). */
export const exportFlatExcel = ({ sheetName, fileName, headers, rows, mapRow }) =>
  exportGroupedExcel({
    sheetName,
    fileName,
    headers,
    groups: [{ key: null, label: null, rows: rows || [] }],
    mapRow,
  });
